import React, { useState } from "react";
import { Loader2, Mail, Lock, User as UserIcon, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

function formatDetail(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" · ");
  }
  return String(detail);
}

export default function AuthDialog({ open, onOpenChange, onAuthed, title }) {
  const [mode, setMode] = useState("signup"); // "signup" | "login"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { signup, login } = useAuth();

  const reset = () => {
    setEmail("");
    setPassword("");
    setName("");
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      let user;
      if (mode === "signup") {
        user = await signup(email.trim(), password, name.trim() || undefined);
        toast.success(`Welcome ${user.name || user.email}!`);
      } else {
        user = await login(email.trim(), password);
        toast.success(`Signed in as ${user.email}`);
      }
      reset();
      onOpenChange?.(false);
      onAuthed?.(user);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange?.(v);
      }}
    >
      <DialogContent
        className="bg-slate-950 border-slate-800 text-slate-100 max-w-md rounded-sm"
        data-testid="auth-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="h-7 w-7 grid place-items-center bg-cyan-500 text-slate-950 rounded-sm">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-base tracking-tight">
              3C<span className="text-cyan-400"> AI</span>
            </span>
          </div>
          <DialogTitle className="font-display text-2xl">
            {title || (mode === "signup" ? "Create your account" : "Welcome back")}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            {mode === "signup"
              ? "Paid plans are tied to your email so your credits stay with you across devices."
              : "Log in with your email to access your paid credits and history."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 pt-2">
          {mode === "signup" && (
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
                Name (optional)
              </Label>
              <div className="relative mt-1">
                <UserIcon className="h-3.5 w-3.5 text-slate-600 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="auth-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="pl-9 bg-slate-900/60 border-slate-800 text-slate-100 rounded-sm focus-visible:ring-cyan-500"
                />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
              Email
            </Label>
            <div className="relative mt-1">
              <Mail className="h-3.5 w-3.5 text-slate-600 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="auth-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="pl-9 bg-slate-900/60 border-slate-800 text-slate-100 rounded-sm focus-visible:ring-cyan-500"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
              Password
            </Label>
            <div className="relative mt-1">
              <Lock className="h-3.5 w-3.5 text-slate-600 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="auth-password"
                type="password"
                required
                minLength={8}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="pl-9 bg-slate-900/60 border-slate-800 text-slate-100 rounded-sm focus-visible:ring-cyan-500"
              />
            </div>
          </div>

          <Button
            data-testid="auth-submit"
            type="submit"
            disabled={busy}
            className="w-full mt-2 rounded-sm bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"
          >
            {busy ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                {mode === "signup" ? "Creating account…" : "Signing in…"}
              </>
            ) : mode === "signup" ? (
              "Create account & continue"
            ) : (
              "Sign in"
            )}
          </Button>
        </form>

        <div className="text-center text-xs text-slate-500 pt-1">
          {mode === "signup" ? (
            <>
              Already have an account?{" "}
              <button
                data-testid="auth-switch-login"
                type="button"
                onClick={() => setMode("login")}
                className="text-cyan-400 hover:underline"
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              New here?{" "}
              <button
                data-testid="auth-switch-signup"
                type="button"
                onClick={() => setMode("signup")}
                className="text-cyan-400 hover:underline"
              >
                Create an account
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
